import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ui.css';

// Kanonische Combobox (Frontend Phase 2/3, F5): durchsuchbares Single-Select
// mit Tastaturbedienung. Erster Abnehmer: CustomerSelect (#35 Stufe 2).
//
// Props:
//   options       – [{ value, label }]
//   value         – aktuell gewählter value (oder null)
//   onChange      – (valueOrNull) => void
//   placeholder   – Text im leeren Eingabefeld
//   disabled      – Bedienung sperren
//   allowClear    – "leeren"-Eintrag anbieten (onChange(null))
//   clearLabel    – Beschriftung des Leeren-Eintrags (Default '— kein —')
//   noResultsText – Text bei leerem Filterergebnis
//   inputId       – id fürs <input> (Label-Kopplung)
const Combobox = ({
  options = [],
  value = null,
  onChange,
  placeholder = '',
  disabled = false,
  allowClear = false,
  clearLabel = '— kein —',
  noResultsText = 'Keine Treffer',
  inputId
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const listIdRef = useRef(`ui-combobox-list-${Math.random().toString(36).slice(2, 9)}`);

  const selected = useMemo(() => options.find(o => o.value === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? options.filter(o => String(o.label || '').toLowerCase().includes(q)) : options;
    return allowClear ? [{ value: null, label: clearLabel, isClear: true }, ...base] : base;
  }, [options, query, allowClear, clearLabel]);

  // Außenklick schließt
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Markierte Option sichtbar halten
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlight]);

  const pick = (opt) => {
    if (typeof onChange === 'function') onChange(opt.isClear ? null : opt.value);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) pick(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="ui-combobox" ref={rootRef}>
      <input
        id={inputId}
        type="text"
        className="ui-combobox-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listIdRef.current}
        aria-autocomplete="list"
        placeholder={selected ? selected.label : placeholder}
        value={open ? query : (selected ? selected.label : '')}
        onChange={(e) => { setQuery(e.target.value); setHighlight(allowClear ? 1 : 0); if (!open) setOpen(true); }}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); setHighlight(0); } }}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
      {open && (
        <ul className="ui-combobox-list" role="listbox" id={listIdRef.current} ref={listRef}>
          {filtered.length === 0 && (
            <li className="ui-combobox-empty">{noResultsText}</li>
          )}
          {filtered.map((opt, idx) => (
            <li
              key={opt.isClear ? '__clear__' : String(opt.value)}
              role="option"
              aria-selected={opt.value === value}
              className={`ui-combobox-option${idx === highlight ? ' highlighted' : ''}${opt.value === value ? ' selected' : ''}${opt.isClear ? ' is-clear' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
              onMouseEnter={() => setHighlight(idx)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Combobox;
